/**
 * Pure helpers for the slash-command dispatcher.
 *
 * The shell's ``handleCommand`` (VariantCShell.tsx) composes these to
 * resolve a parsed ``CommandIntent`` into either a state change or a
 * user-facing error string. Extracted here so every branch is unit-
 * testable without a React tree — the shell's consumption is then a
 * thin wrapper that also performs the side-effects (dispatch, Word
 * scroll).
 *
 * SRP: these helpers know the ``sections`` shape and the command
 * vocabulary; they do NOT know React, dispatch, Office.js, or the
 * rail. Every function is pure.
 *
 * # DECISION: surface a string | null for "dispatch result" rather
 * #   than throwing or returning a typed enum.
 * # WHY: the rail already has a single error-hint surface. Returning
 * #      the writer-facing message directly keeps the contract
 * #      minimal and makes every call site's failure mode visible at
 * #      the call — no exception flow, no sentinel codes.
 * # DATE: 2026-04-22
 */

/** Narrow shape we need — kept local so we don't pull the full
 *  SpineSection type (or its import graph) into tests. The shell
 *  passes its richer objects; structural typing handles the
 *  widening automatically. */
export interface JumpTargetSection {
  id: string;
  number: string;
  status?: string;
}

/**
 * Resolve a /jump argument (``§9.2`` or ``CON-401`` or bare ``9.2``)
 * to a section object, or null when no section matches.
 *
 * Matching rules:
 *   - exact id match (``CON-401``)
 *   - number match with optional leading ``§`` stripped (``§9.2``
 *     matches a section whose ``number`` is ``"9.2"``)
 *
 * Pure; deterministic; no side-effects.
 */
export function resolveJumpTarget<S extends JumpTargetSection>(
  sections: ReadonlyArray<S>,
  sectionArg: string,
): S | null {
  const trimmed = sectionArg.trim();
  // Trim must happen BEFORE the § strip — a leading space would
  // otherwise hide the §, leaving the bareNumber unchanged and the
  // lookup silently missing valid writer input like " §9.2 ".
  const bareNumber = trimmed.replace(/^§/, "");
  return sections.find((s) => s.id === trimmed || s.number === bareNumber) ?? null;
}

/**
 * Resolve a /next target — the first section still needing writer
 * input, else the first queued section, else the first section of
 * all. Returns null only when the list is empty.
 *
 * Pure; matches the shell's existing fallback chain so the behaviour
 * is testable in isolation.
 */
export function resolveNextTarget<S extends JumpTargetSection>(sections: ReadonlyArray<S>): S | null {
  return (
    sections.find((s) => s.status === "needs_approval") ??
    sections.find((s) => s.status === "queued") ??
    sections[0] ??
    null
  );
}

/**
 * Writer-facing error string for a /jump that didn't match any
 * section. Shown in the rail's error-hint banner.
 *
 * Kept as a named helper so tests pin the copy (regressions on
 * writer-visible strings should be deliberate).
 */
export function buildJumpErrorMessage(sectionArg: string): string {
  return `No section matches "${sectionArg}". Try /jump §9.2 or /jump CON-401.`;
}

/**
 * Writer-facing error string for commands whose parser recognises
 * them but whose handler isn't wired yet (currently only /undo).
 *
 * # DECISION: keep unimplemented commands in the parser + menu
 * #   (marked disabled) rather than deleting them.
 * # WHY: OCP — the parser's job is to recognise valid shapes; the
 * #      writer discovering "/undo" in the menu and seeing
 * #      "coming soon" is a better first-touch experience than
 * #      silent rejection. Handler's error string completes the
 * #      honesty loop for writers who type it manually.
 * # DATE: 2026-04-22
 */
export function buildComingSoonMessage(commandName: string): string {
  return `/${commandName} isn't available yet — coming soon.`;
}
